import { ErrorStrategy, FliptClient } from "@flipt-io/flipt-client-js";

export const FLIPT_NAMESPACE = "default";

// Static entityId — per-user targeting is not supported yet. The SDK requires a non-empty entityId.
const ENTITY_ID = "default";

export class FliptService {
  private client: FliptClient | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Starts Flipt initialisation. Call without awaiting — app startup is never blocked (the provider
   * renders per-flag defaults immediately and swaps to live values once the snapshot loads). A slow
   * but successful init is adopted rather than discarded, so a remote Flipt that takes longer than a
   * frame still ends up driving evaluations.
   */
  init(url: string, environment: string): Promise<void> {
    this.initPromise = FliptClient.init({
      url,
      namespace: FLIPT_NAMESPACE,
      environment,
      errorStrategy: ErrorStrategy.Fallback,
      // updateInterval is a no-op in the browser (SDK auto-refresh is Node-only), so the browser
      // snapshot only changes when refresh() is called — FeatureFlagProvider drives that on a timer.
      updateInterval: 0,
    })
      .then((client) => {
        this.client = client;
        console.info(`[FliptService] connected — snapshot loaded (env=${environment})`);
      })
      .catch((error) => {
        console.warn("[FliptService] init failed — flags serve their per-flag defaults", error);
      });
    return this.initPromise;
  }

  /** Resolves when init has settled — immediately if init was never called. */
  whenReady(): Promise<void> {
    return this.initPromise ?? Promise.resolve();
  }

  /**
   * Re-fetches the flag snapshot (ETag-cheap when unchanged).
   *
   * @returns true when the snapshot changed; false when unchanged, uninitialised, or on error.
   */
  async refresh(): Promise<boolean> {
    if (!this.client) {
      return false;
    }
    try {
      return await this.client.refresh();
    } catch (error) {
      console.warn("[FliptService] refresh failed — keeping the current snapshot", error);
      return false;
    }
  }

  evaluateBoolean(flagKey: string, fallback: boolean): boolean {
    if (!this.client) {
      return fallback;
    }
    try {
      return this.client.evaluateBoolean({ flagKey, entityId: ENTITY_ID, context: {} }).enabled;
    } catch (error) {
      console.warn(`[FliptService] evaluateBoolean failed for ${flagKey}, serving fallback`, error);
      return fallback;
    }
  }

  evaluateVariant(flagKey: string, fallbackVariant: string): string {
    if (!this.client) {
      return fallbackVariant;
    }
    try {
      const result = this.client.evaluateVariant({ flagKey, entityId: ENTITY_ID, context: {} });
      // Keep the fallback in code, not Flipt: a non-match still carries Flipt's stored default
      // variant, so trusting it would let a misconfigured flag silently override the code default.
      return result.match ? result.variantKey : fallbackVariant;
    } catch (error) {
      console.warn(`[FliptService] evaluateVariant failed for ${flagKey}, serving fallback`, error);
      return fallbackVariant;
    }
  }
}

export const fliptService = new FliptService();
